<template>
  <v-app v-if="appConfig">
    <vue-snotify />

    <app-navigation-drawer
      :items="appConfig.navigationDrawerItems"
      :navigation-drawer.sync="navigationDrawer"
      @select:device:terminal="onSelectDeviceTerminal"
      @app:exit="onAppExit"
    />

    <app-toolbar
      :items="appConfig.toolbarNavigationItems"
      :navigation-drawer.sync="navigationDrawer"
      :toolbar-title="toolbarTitle"
      @app:logout="showLogOutDialog"
    />

    <v-main>
      <slot :navigation-drawer="navigationDrawer" />

      <customer-add-dialog
        v-if="posUserHasComponentPermission('Customers', 'MANAGE')"
        :preset-data="customerAddPresetData"
        :visible.sync="customerAddDialogVisible"
      />

      <customer-details-dialog
        v-if="posUserHasComponentPermission('CustomerDetails', 'ACCESS')"
        :customer-id="selectedCustomerId"
        :visible.sync="customerDetailsDialogVisible"
      />

      <order-details-dialog
        :display-order="selectedOrder"
        :order-details-dialog-visible.sync="orderDetailsDialogVisible"
      />

      <confirm-dialog
        :html-content="`${$t('Common.Misc.LogoutDialog.Body')}<br>`"
        :html-title="`${$t('Common.Misc.LogoutDialog.Title')}`"
        :info-bar-body="$t('Common.Misc.LogoutDialog.Warning')"
        :title-class="'red white--text lighten-0'"
        :visible.sync="confirmDialogLogOutVisible"
        info-bar
        style="z-index: 2147483647;"
        yes-button-event="logout-yes"
        @logout-yes="onLogOutYes"
      />

      <confirm-dialog
        :html-content="`${$t('Common.Misc.ExitAppDialog.Body')}<br>`"
        :html-title="`${$t('Common.Misc.ExitAppDialog.Title')}`"
        :info-bar-body="$t('Common.Misc.ExitAppDialog.Warning')"
        :title-class="'red white--text lighten-0'"
        :visible.sync="confirmDialogExitAppVisible"
        info-bar
        style="z-index: 2147483647;"
        title-icon="exit_to_app"
        yes-button-event="exit-yes"
        @exit-yes="onExitAppYes"
      />

      <manager-pin-dialog
        :data="managerPinDialogData"
        :visible.sync="confirmManagerPinDialogVisible"
      />

      <reservation-add-dialog
        v-if="reservationAddDialogVisible"
        :area="reservationTableArea"
        :caller-id="reservationCallerID"
        :customer="reservationCustomer"
        :reservation-to-edit="reservationToEdit"
        :table="reservationTable"
        :visible.sync="reservationAddDialogVisible"
      />

      <customer-email-select-dialog
        :data="customerSelectEmailDialogData"
        :visible.sync="customerSelectEmailDialogVisible"
        @save:emails="emailFiscalDocumentConfirm"
      />

      <customer-company-select-dialog
        v-if="customerSelectCompanyDialogVisible"
        :data="customerSelectCompanyDialogData"
        :visible.sync="customerSelectCompanyDialogVisible"
        @save:company="createOrConvertFiscalDocument"
        @cancel:company="cancelSelectCompanyForInvoiceDialog"
      />

      <select-payment-terminal-dialog
        :filter-vendor="TerminalVendorEnum.VIVA"
        :visible.sync="selectDeviceTerminalVisible"
        hide-instalments
        @terminal:selected="onSetActiveDeviceTerminal"
      />

      <confirm-dialog
        :cancel-button="selectedOrderHasRefundableTransactions"
        :cancel-button-text="$t('Common.Button.Close').toLocaleUpperCase($i18n.locale)"
        :html-content="selectedOrderHasRefundableTransactions ? $t('Order.OrderDetailsDialog.Receipt.ConfirmDialog.CancelWithRefund.Content', {amount: $options.filters.currency(selectedOrderRefundableTransactionsTotal)}) : $t('Order.OrderDetailsDialog.Receipt.ConfirmDialog.Cancel.Content')"
        :html-title="$t('Order.OrderDetailsDialog.Receipt.ConfirmDialog.Cancel.Title')"
        :info-bar="true"
        :info-bar-body="selectedOrderHasRefundableTransactions ? $t('Order.OrderDetailsDialog.Receipt.ConfirmDialog.CancelWithRefund.Notification') : $t('Order.OrderDetailsDialog.Receipt.ConfirmDialog.Cancel.Notification')"
        :info-bar-class="isDarkMode ? 'red lighten-2' : 'red lighten-4'"
        :no-button-event="selectedOrderHasRefundableTransactions ? 'on-confirm-cancel-receipt' : 'confirm-dialog-no'"
        :no-button-text="selectedOrderHasRefundableTransactions ? $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale) : $t('Common.Button.No').toLocaleUpperCase($i18n.locale)"
        :visible.sync="confirmCancelFiscalDocDialogVisible"
        :width="560"
        :yes-button-event="selectedOrderHasRefundableTransactions ? 'on-confirm-cancel-receipt-refund' : 'on-confirm-cancel-receipt'"
        :yes-button-text="selectedOrderHasRefundableTransactions ? $t('Common.Button.CancelWithRefund').toLocaleUpperCase($i18n.locale) : $t('Common.Button.Cancel').toLocaleUpperCase($i18n.locale)"
        no-button-class="v-btn--outline grey--text"
        title-class="red white--text lighten-0"
        yes-button-class="red white--text"
        @on-confirm-cancel-receipt-refund="cancelFiscalDocument(selectedReceiptItem, true)"
        @on-confirm-cancel-receipt="cancelFiscalDocument(selectedReceiptItem, false)"
      />

      <!--Settings Wizard Dialog -->
      <settings-wizard-dialog
        v-if="userIsOwner || userIsSuperAdmin"
        :visible.sync="settingsWizardDialogVisible"
      />
    </v-main>

    <v-footer
      id="appFooter"
      app
      class="secondary white--text gfp-footer px-1"
      color="grey"
      fixed
    >
      <template v-if="userIsWaiter && canSeeActiveElectronWorkingStations && !hasActiveElectronWorkingStation.AllActive">
        <v-alert
          border="left"
          class="mx-1 mt-0 r mb-3 px-2 py-1 rounded-md text-center v-alert--electron"
          icon="mdi-printer-pos-alert-outline"
          prominent
          type="error"
          width="100%"
        >
          <div
            class="font-weight-bold"
            style="font-size: 14px;"
          >
            {{ $t('Common.Error.Offline.Electron.Title') }}
          </div>
          <div
            v-if="!!hasActiveElectronWorkingStation.MissingMachines.length"
            class="caption"
          >
            {{ $t('Common.Error.Offline.Electron.WorkStations') }} : {{ hasActiveElectronWorkingStation.MissingMachines.join(', ') }}
          </div>
        </v-alert>
      </template>

      <template
        v-if="envWarningVisible && ((isProduction && appDomainIsDev) || (isDevelopment && (appDomainIsLive || appDomainIsTrial)) || (isStaging && (appDomainIsLive || appDomainIsTrial)))"
      >
        <v-alert
          v-model="envWarningVisible"
          border="left"
          class="mx-1 mt-0 r mb-3 px-2 py-1 rounded-md text-center"
          dense
          dismissible
          icon="error"
          type="warning"
          width="100%"
          @input="onEnvWarning"
        >
          <template v-if="isDevelopment && (appDomainIsLive || appDomainIsTrial)">
            <span v-if="appDomainIsLive"><strong>Attention:</strong> You are accessing a <strong>LIVE DOMAIN</strong> from <strong>DEVELOPMENT</strong> Environment</span>
            <span v-if="appDomainIsTrial"><strong>Attention:</strong> You are accessing a <strong>TRIAL DOMAIN</strong> from <strong>DEVELOPMENT</strong> Environment</span>
          </template>

          <template v-if="isStaging && (appDomainIsLive || appDomainIsTrial)">
            <span v-if="appDomainIsLive"><strong>Attention:</strong> You are accessing a <strong>LIVE DOMAIN</strong> from <strong>STAGING</strong> Environment</span>
            <span v-if="appDomainIsTrial"><strong>Attention:</strong> You are accessing a <strong>TRIAL DOMAIN</strong> from <strong>STAGING</strong> Environment</span>
          </template>

          <template v-if="isProduction && (appDomainIsDev)">
            <span v-if="appDomainIsDev"><strong>Attention:</strong> You are accessing a <strong>DEV DOMAIN</strong> from <strong>PRODUCTION</strong> Environment</span>
          </template>
        </v-alert>
      </template>

      <div
        class="gfp-footer text-center"
        style="width: 100%;"
      >
        © {{ new Date().getFullYear() }} <a
          class="gfp-footer"
          href="https://www.otodev.gr/"
          target="_blank"
        ><strong>OTO Dev</strong></a> - Powered By <a
          class="gfp-footer"
          href="https://getfoodpro.gr/"
          target="_blank"
        ><strong>GETFOOD Pro</strong></a>

        <div
          v-if="posUserCan('Account', 'ACCESS')"
          class="float-right mx-1"
        >
          <v-menu
            v-model="themeMenu"
            :close-on-content-click="false"
            min-width="300"
          >
            <template #activator="{on, attrs}">
              <v-btn
                dark
                depressed
                icon
                v-bind="attrs"
                x-small
                v-on="on"
              >
                <v-icon>mdi mdi-invert-colors</v-icon>
              </v-btn>
            </template>

            <v-card>
              <v-card-title class="text-body-2 font-weight-bold pa-3 pb-1">
                {{ $t('Account.Overview.Field.Theme.Title') }}

                <v-spacer />

                <v-btn
                  icon
                  small
                  @click="themeMenu = false"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>

              <v-divider />

              <v-item-group
                v-if="posUser"
                v-model="posUser.theme_mode"
                mandatory
                @change="onSelectThemeClick"
              >
                <v-container fluid>
                  <v-row>
                    <v-col
                      v-for="theme in $t('Account.Overview.Field.Theme.Items')"
                      :key="theme.Value"
                      cols="4"
                    >
                      <v-item
                        v-slot="{active, toggle}"
                        :value="theme.Value"
                      >
                        <v-card
                          :dark="theme.Value === 'auto' ? isSystemThemeDark() : theme.Value !== 'light'"
                          :light="theme.Value === 'auto' ? !isSystemThemeDark() : theme.Value === 'light'"
                          :style="active ? 'border: #59ae4a solid 2px;' : 'border: #3b98f6 solid 2px;'"
                          :value="theme.Value"
                          max-width="85"
                          width="85"
                          @click="toggle"
                        >
                          <div class="pa-2">
                            <v-img :src="`/assets/img/${theme.Value}-mode.svg`" />

                            <v-avatar
                              v-if="active"
                              color="light-green lighten-4"
                              size="25"
                              style="position: absolute; left: -12px; top: -12px;"
                            >
                              <v-icon
                                color="success"
                              >
                                mdi-check-circle
                              </v-icon>
                            </v-avatar>
                          </div>

                          <v-divider />

                          <div class="align-start d-flex px-3 py-1">
                            <div class="d-inline-flex">
                              <div>
                                <div class="text-body-2">
                                  {{ theme.Label }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </v-card>
                      </v-item>
                    </v-col>
                  </v-row>
                </v-container>
              </v-item-group>
            </v-card>
          </v-menu>
        </div>
      </div>
    </v-footer>

    <offline-modal :visible="offlineModalVisible" />

    <loading-modal
      :title="showLoaderTitle"
      :visible="showLoader"
    />
  </v-app>
</template>

<script>
import ReceiptsGlobal from '@/mixins/receipts/receiptsGlobal'
import AuthLayoutsCommon from '@/mixins/layouts/authLayoutsCommon.js'
import FooterLayoutsCommon from '@/mixins/layouts/footerLayoutsCommon.js'
import WizardLayoutsCommon from '@/mixins/layouts/wizardLayoutsCommon.js'
import CustomerLayoutsCommon from '@/mixins/layouts/customerLayoutsCommon.js'
import OrderLayoutsCommon from '@/mixins/layouts/orderLayoutsCommon.js'
import ManagerPinLayoutsCommon from '@/mixins/layouts/managerPinLayoutsCommon.js'
import TerminalVendorEnum from '@/api/Enums/TerminalVendorEnum.js'
import NavigationDrawerLayoutsCommon from '@/mixins/layouts/navigationDrawerLayoutsCommon.js'
import DeviceTerminalLayoutsCommon from '@/mixins/layouts/deviceTerminalLayoutsCommon.js'
import AppToolbarLayoutsCommon from '@/mixins/layouts/appToolbarLayoutsCommon.js'
import ReservationLayoutsCommon from '@/mixins/layouts/reservationLayoutsCommon.js'

export default {
  name      : 'DefaultLayout',
  components: {},

  mixins: [
    AuthLayoutsCommon,
    FooterLayoutsCommon,
    WizardLayoutsCommon,
    OrderLayoutsCommon,
    CustomerLayoutsCommon,
    ManagerPinLayoutsCommon,
    NavigationDrawerLayoutsCommon,
    DeviceTerminalLayoutsCommon,
    AppToolbarLayoutsCommon,
    ReservationLayoutsCommon,
    ReceiptsGlobal
  ],

  data() {
    return {
      envWarningVisible: true
    }
  },
  computed: {
    TerminalVendorEnum() {
      return TerminalVendorEnum
    }
  },

  watch: {},

  created() {
    console.log('appConfig::', this.appConfig)
    console.log(`%c${'-'.repeat(15)} APP CREATED ${this.appVersion} (${this.$options.name}) ${this.appConfig?.LOCATION_DATA.SiteUrl} ${'-'.repeat(15)}`, 'background-color: #388E3C; color: #A5D6A7; padding: 10px 10px;')
  },

  mounted() {
  },

  updated() {
    if (typeof window === 'undefined') return
    window.dispatchEvent(new Event('resize'))
  },

  beforeDestroy() {
    console.log('%c--------------- APP DESTROYED ---------------', 'background-color: #E53935; color: #EF9A9A; padding: 10px 10px;')
  },
  methods: {
    onEnvWarning() {
      this.$nextTick(() => {
        const element = document.getElementsByClassName('v-main')[0]
        if (element) element.style.paddingBottom = '0px'
      })
    }
  }

}

</script>

<style>
.v-data-table__wrapper > table > tbody > tr > td {
  padding: 0 8px;
  min-height: 48px;
}

.v-application--is-ltr .v-data-footer__pagination {
  margin: 0 8px 0 2px;
}

.filter-toolbar .v-toolbar__content {
  padding: 0 6px;
}

@media only screen and (max-width: 599px) {
  .v-data-table__wrapper > table > tbody > tr > td {
    padding: 0 8px;
    min-height: 22px !important;
  }

  .v-data-table__mobile-row__header, .v-data-table__mobile-row__cell {
    font-size: 0.75rem !important;
    line-height: 1.25rem;
    padding: 2px;
  }

  .v-slide-group__next, .v-slide-group__prev {
    flex: 0 1 28px;
    min-width: 28px;
  }
}

.gfp-footer {
  font-size: 14px;
  color: #FFFFFF;
}

.gfp-footer > a,
.gfp-footer > a:link,
.gfp-footer > a:visited,
.gfp-footer > a:active {
  text-decoration: none !important;
  color: #FFFFFF !important;
}

.gfp-footer > a:hover {
  text-decoration: underline !important;
  color: #FFFFFF !important;
}

.border-none {
  border: none !important;
}

.border-bottom-none {
  border: none !important;
}

.border-top-none {
  border: none !important;
}

.border-left-none {
  border: none !important;
}

.border-right-none {
  border: none !important;
}


.divTable {
  display: table;
  width: 100%;
  border-spacing: 0;
}

.divTableRow {
  display: table-row;
}

.v-application.theme--dark .divTableBody .divTableRow:nth-child(even) {
  background-color: #212121;
}

.v-application.theme--light .divTableBody .divTableRow:nth-child(even) {
  background-color: #F7F7F7;
}

.v-application.theme--dark .divTableBody .divTableRow:nth-child(odd) {
  background-color: #2c2c2c;
}

.v-application.theme--light .divTableBody .divTableRow:nth-child(odd) {
  background-color: #FFF;
}

.divTableCell, .divTableHead {
  border-collapse: collapse;
  display: table-cell;
  padding: 6px 6px 6px 6px;
  vertical-align: middle;
}

.divTableGroup {
  display: table-row-group;
}

.v-application.theme--dark .divTableCell, .v-application.theme--dark .divTableHead {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.v-application.theme--light .divTableCell, .v-application.theme--light .divTableHead {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.v-application.theme--dark .divTableCell {
  color: white;
}

.v-application.theme--light .divTableCell {
  color: black;
}

.divTableHead {
  color: grey;
  padding: 16px 10px;
  vertical-align: middle;
  white-space: nowrap;
}

.divTableHeading {
  display: table-header-group;
  font-weight: bold;
}

.v-application.theme--dark .divTableHeading, .v-application.theme--dark .divTableFoot {
  background-color: #191919;
}

.v-application.theme--light .divTableHeading, .v-application.theme--dark .divTableFoot {
  background-color: #EEE;
}

.divTableFoot {
  display: table-footer-group;
  font-weight: bold;
}

.divTableBody {
  display: table-row-group;
}

.v-alert--electron .v-alert__icon.v-icon {
  font-size: 28px;
}

.v-alert--electron .v-alert__icon {
  height: 28px;
  min-width: 28px;
}

.v-alert--electron .v-alert__icon {
  margin-right: 8px;
}
</style>
